import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, Eye, ArrowRight, ShieldCheck, FileDown, LayoutTemplate, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { TemplatePreviewDialog, thumbUrl } from "../components/TemplatePreviewDialog";
import { PlanCards } from "../components/PlanCards";

const FEATURES = [
  { icon: LayoutTemplate, title: "25 recruiter-tested designs", body: "Switch templates anytime. Your content reflows instantly." },
  { icon: Sparkles, title: "Live preview as you type", body: "See every change rendered on a real A4 page." },
  { icon: FileDown, title: "Print-perfect PDFs", body: "Crisp, selectable text that passes applicant tracking systems." },
  { icon: ShieldCheck, title: "Your data stays yours", body: "Private by default. Only you can see your resumes." },
];

const Hero = ({ onStart }) => (
  <section className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-16 sm:pt-24 pb-16 grid lg:grid-cols-12 gap-12 items-center">
    <div className="lg:col-span-6 rise">
      <p className="text-xs font-mono uppercase tracking-[0.25em] text-emerald-400 mb-6">Resume builder · PDF in minutes</p>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.02]">
        Fill a form.<br />Get a resume<br /><span className="text-emerald-400">that gets calls.</span>
      </h1>
      <p className="mt-6 text-base md:text-lg text-slate-400 max-w-lg">
        Build for free with a live preview. Unlock unlimited PDF downloads for just $2.99 a month — or $11.99 for the whole year.
      </p>
      <div className="mt-10 flex flex-wrap items-center gap-4">
        <Button onClick={onStart} data-testid="hero-start-btn" className="rounded-full h-12 px-7 bg-emerald-500 text-black font-semibold hover:bg-emerald-400 hover:text-black transition-colors">
          Build my resume <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
        <a href="#pricing" data-testid="hero-pricing-link" className="text-sm text-slate-300 hover:text-white underline-offset-4 hover:underline">See pricing</a>
      </div>
    </div>
    <div className="lg:col-span-6 relative h-[460px] sm:h-[520px]">
      {["creative", "modern", "executive"].map((t, i) => (
        <img key={t} src={thumbUrl(t, "large")} fetchPriority="high" alt={`${t} template`} data-testid={`hero-template-${t}`}
          className="absolute w-[58%] rounded-lg shadow-2xl shadow-black/60 border border-white/10 rise transition-transform duration-500 hover:-translate-y-2"
          style={{ left: `${i * 20}%`, top: `${[40, 0, 60][i]}px`, transform: `rotate(${[-6, 0, 5][i]}deg)`, zIndex: [1, 3, 2][i], animationDelay: `${i * 0.12}s` }} />
      ))}
      <div className="absolute z-10 -left-2 sm:left-[-4%] bottom-2 sm:bottom-6 flex items-center gap-3 rounded-2xl border border-white/10 bg-[#12151E]/90 backdrop-blur-xl p-3 pr-5 shadow-2xl shadow-black/60 rise" style={{ animationDelay: "0.45s" }} data-testid="hero-photo-badge">
        <img src="/hero-photo.jpg" alt="Profile photo on resume"
          className="h-16 w-16 rounded-md object-cover ring-4 ring-emerald-500/80" />
        <div>
          <p className="text-sm font-semibold flex items-center gap-1.5"><Camera className="h-3.5 w-3.5 text-emerald-400" />Add your photo</p>
          <p className="text-xs text-slate-400">Placed perfectly on every template</p>
        </div>
      </div>
    </div>
  </section>
);

const Features = () => (
  <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 grid md:grid-cols-4 gap-px bg-white/5 rounded-2xl overflow-hidden border border-white/5">
    {FEATURES.map(({ icon: Icon, title, body }, i) => (
      <div key={title} className={`bg-[#0c0e14] p-8 ${i === 0 ? "md:col-span-2" : ""} ${i === 3 ? "md:col-span-2" : ""}`}>
        <Icon className="h-5 w-5 text-emerald-400" />
        <h3 className="mt-5 text-lg font-semibold">{title}</h3>
        <p className="mt-2 text-sm text-slate-400">{body}</p>
      </div>
    ))}
  </section>
);

const Gallery = ({ templates, onOpen }) => (
  <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
    <p className="text-xs font-mono uppercase tracking-[0.25em] text-emerald-400">Templates</p>
    <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight">25 designs. One form.</h2>
    <div className="mt-10 grid grid-cols-2 md:grid-cols-5 gap-5">
      {templates.map((t) => (
        <figure key={t.id} className="group cursor-zoom-in" data-testid={`gallery-template-${t.id}`} onClick={() => onOpen(t)}>
          <div className="relative rounded-lg overflow-hidden border border-white/10 bg-white transition-transform duration-300 group-hover:-translate-y-1.5">
            <img src={thumbUrl(t.id)} alt={t.name} className="w-full aspect-[210/297] object-cover object-top" loading="lazy" decoding="async" />
            <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/40 transition-colors">
              <span className="opacity-0 group-hover:opacity-100 transition-opacity rounded-full bg-white text-black text-xs font-semibold px-4 py-2 flex items-center gap-1.5"><Eye className="h-3.5 w-3.5" />Preview</span>
            </span>
          </div>
          <figcaption className="mt-3 text-sm font-medium">{t.name}</figcaption>
          <p className="text-xs text-slate-500">{t.tag}</p>
        </figure>
      ))}
    </div>
  </section>
);

export default function Landing() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  useEffect(() => { api.get("/templates").then((r) => setTemplates(r.data)); }, []);
  const [zoom, setZoom] = useState(null);
  const start = () => (user ? navigate("/dashboard") : login());
  const useTemplate = async (template) => {
    if (!user) return login(`/dashboard?template=${template}`);
    const { data } = await api.post("/resumes", { title: "My resume", template });
    navigate(`/builder/${data.resume_id}`);
  };

  return (
    <main className="glow relative z-10" data-testid="landing-page">
      <Hero onStart={start} />
      <Features />
      <Gallery templates={templates} onOpen={setZoom} />
      <TemplatePreviewDialog template={zoom} onClose={() => setZoom(null)} onUse={useTemplate} />
      <section id="pricing" className="max-w-5xl mx-auto px-5 sm:px-8 py-20">
        <p className="text-xs font-mono uppercase tracking-[0.25em] text-emerald-400">Pricing</p>
        <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight">Build free. Pay only to download.</h2>
        <div className="mt-10"><PlanCards onSelect={(p) => (user ? navigate(`/pricing?plan=${p}`) : login(`/pricing?plan=${p}`))} /></div>
      </section>
      <footer className="border-t border-white/5 py-10 text-center text-xs text-slate-500">© {new Date().getFullYear()} Rezume Ink</footer>
    </main>
  );
}
